import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations, LANGUAGES } from "@/i18n/translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "kompass.lang";

const getInitialLang = () => {
  if (typeof window === "undefined") return "de";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && translations[saved]) return saved;
  return "de";
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(getInitialLang);

  const langMeta = useMemo(
    () => LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0],
    [lang]
  );

  const setLang = useCallback((next) => {
    if (!translations[next]) return;
    setLangState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
    document.documentElement.dir = langMeta.dir;
    document.documentElement.dataset.lang = lang;
  }, [lang, langMeta.dir]);

  const t = useMemo(() => translations[lang] || translations.en, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t, dir: langMeta.dir, langMeta, languages: LANGUAGES }),
    [lang, setLang, t, langMeta]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
};
